/**
 * Goal entries list — shows entries for numeric or steps goals.
 * Numeric: entry rows sorted by date (newest first).
 * Steps: interactive checklist.
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence } from 'framer-motion'
import type { Goal, GoalEntry } from '@turnifly/shared/types'
import { GoalEntryRow } from './goal-entry-row'
import { GoalStepsChecklist } from './goal-steps-checklist'
import { useDeleteEntry, useShareEntry } from '@turnifly/shared/hooks'

interface GoalEntriesListProps {
  goal: Goal
  entries: GoalEntry[]
  isOwner: boolean
  maxItems?: number
  showAll?: boolean
}

export function GoalEntriesList({
  goal,
  entries,
  isOwner,
  maxItems,
  showAll = false,
}: GoalEntriesListProps): React.ReactElement {
  const { t } = useTranslation()
  const deleteEntry = useDeleteEntry()
  const shareEntry = useShareEntry()

  const handleDelete = useCallback(
    (entryId: string): void => {
      deleteEntry.mutate({ entryId, goalId: goal.id })
    },
    [deleteEntry, goal.id],
  )

  const handleShare = useCallback(
    (entry: GoalEntry): void => {
      shareEntry.mutate({ entry, goal })
    },
    [shareEntry, goal],
  )

  // Steps mode → checklist
  if (goal.goal_type === 'steps') {
    return (
      <GoalStepsChecklist goal={goal} entries={entries} isOwner={isOwner} />
    )
  }

  // Numeric mode → entry rows
  const displayEntries = showAll
    ? entries
    : maxItems
      ? entries.slice(0, maxItems)
      : entries

  if (displayEntries.length === 0) {
    return (
      <p className="py-3 text-center text-sm text-white/30">
        {t('goals.noEntries')}
      </p>
    )
  }

  return (
    <div className="space-y-1.5">
      <AnimatePresence mode="popLayout">
        {displayEntries.map((entry) => (
          <GoalEntryRow
            key={entry.id}
            entry={entry}
            goal={goal}
            isOwner={isOwner}
            onShare={isOwner ? handleShare : undefined}
            onDelete={isOwner ? handleDelete : undefined}
          />
        ))}
      </AnimatePresence>

      {!showAll && maxItems && entries.length > maxItems && (
        <p className="pt-1 text-center text-xs text-primary/60">
          +{entries.length - maxItems} {t('goals.showAllEntries')}
        </p>
      )}
    </div>
  )
}
